import { create } from "zustand";
import { persist } from "zustand/middleware";

interface User {
  email: string;
  name: string;
  userId: string;
  role: string;
  isPremium: boolean;
  isDetailsSaved: boolean;
  accessToken?: string;
  premiumUntil?: string;
  unReadMessages?: number;
}

interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  isDetailsSaved?: boolean;
  accessToken: string | null;
  userId: string | null;
  role: string | null;
  isPremium: boolean;
  theme: string;
  premiumFeatures: string[];
  wasAutoLoggedOut: boolean;
  setWasAutoLoggedOut: (value: boolean) => void;
  login: (user: User) => void;
  logout: () => void;
  autoLogout: () => void;
  setPremium: (value: boolean) => void;
  setTheme: (theme: string) => void;
  hasPremiumAccess: (feature?: string) => boolean;
  checkPremiumStatus: () => boolean;
  updatePremiumExpiry: (date: string) => void;
  unReadMessages: number;
  updateUnReadMessages: (count: number) => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      accessToken: null,
      userId: null,
      role: null,
      isPremium: false,
      theme: "light",
      premiumFeatures: [
        "ai-agents",
        "advanced-analytics",
        "unlimited-projects",
        "job-matching",
      ],
      wasAutoLoggedOut: false,
      unReadMessages: 0,
      login: (user) =>
        set({
          user,
          isAuthenticated: true,
          isPremium: user.isPremium,
          accessToken: user.accessToken || null,
          userId: user.userId || null,
          role: user.role || null,
          isDetailsSaved: user.isDetailsSaved ?? true,
          wasAutoLoggedOut: false,
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isPremium: false,
          accessToken: null,
          userId: null,
          role: null,
          wasAutoLoggedOut: false,
        }),

      autoLogout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isPremium: false,
          accessToken: null,
          userId: null,
          role: null,
          wasAutoLoggedOut: true,
        }),
        
      setWasAutoLoggedOut: (value) => set({ wasAutoLoggedOut: value }),

      setPremium: (value) =>
        set((state) => ({
          isPremium: value,
          user: state.user ? { ...state.user, isPremium: value } : null,
        })),

      setTheme: (theme) => set({ theme }),

      hasPremiumAccess: (feature) => {
        const state = get();
        if (!feature) return state.isPremium;
        return state.isPremium && state.premiumFeatures.includes(feature);
      },

      checkPremiumStatus: () => {
        const state = get();
        if (!state.user) return false;

        if (state.user.premiumUntil) {
          const now = new Date();
          const expiryDate = new Date(state.user.premiumUntil);
          if (now > expiryDate) {
            set((state) => ({
              isPremium: false,
              user: state.user ? { ...state.user, isPremium: false } : null,
            }));
            return false;
          }
        }

        return state.isPremium;
      },
      updateUnReadMessages: (count) => {
        set((state) => ({
          user: state.user ? { ...state.user, unReadMessages: count } : null,
          unReadMessages:  count ?? 0,
        }));
      },

      updatePremiumExpiry: (date) =>
        set((state) => ({
          user: state.user ? { ...state.user, premiumUntil: date } : null,
        })),
    }),
    {
      name: "user-storage",
    }
  )
);
